import { RECAPTCHA_SITE_KEY } from '../config/env';

/**
 * reCAPTCHA yardımcıları.
 *
 * - **v3** (görünmez): `getRecaptchaToken` Google betiğini ilk kullanımda enjekte eder ve
 *   `grecaptcha.execute` ile bir skor token'ı alır. Tamamen opsiyonel — site key yoksa
 *   `undefined` döner ve login akışı captcha'sız devam eder.
 * - **v2** (görünür kutu): `RecaptchaCheckbox` bileşeni `api.js?render=explicit`'i yükleyip
 *   `grecaptcha.render` ile widget'ı çizer. İki sürüm aynı `window.grecaptcha` global'ini
 *   paylaştığı için tip burada tek yerde tanımlı.
 */
export interface Grecaptcha {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
  render: (
    container: HTMLElement,
    params: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'light' | 'dark';
      size?: 'normal' | 'compact';
    }
  ) => number;
  reset: (widgetId?: number) => void;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
    __tfwOnRecaptchaV2Load?: () => void;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(siteKey: string): Promise<void> {
  if (window.grecaptcha) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('reCAPTCHA yüklenemedi'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export async function getRecaptchaToken(action = 'login'): Promise<string | undefined> {
  if (!RECAPTCHA_SITE_KEY) return undefined;
  try {
    await loadScript(RECAPTCHA_SITE_KEY);
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) return undefined;
    await new Promise<void>((resolve) => grecaptcha.ready(resolve));
    return await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
  } catch {
    return undefined;
  }
}
